import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { ArrowLeft, RefreshCw, ExternalLink, Plus, Trash2, Package, Printer, X, MapPin, ArrowRight, Truck, Pencil, CreditCard, ImagePlus, CheckCircle2, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateTransferDialog } from "@/components/CreateTransferDialog";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (v) =>
  v != null && v !== ""
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(v)
    : "—";

const STATUS_STYLE = {
  Active:    "bg-green-50 text-green-700 border-green-200",
  Completed: "bg-blue-50 text-blue-700 border-blue-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

const ACT_DOT = {
  created:           "bg-[#1B2A6B]",
  load_added:        "bg-green-500",
  load_removed:      "bg-red-500",
  transfer_created:  "bg-purple-500",
  payment_added:     "bg-emerald-500",
  updated:           "bg-orange-400",
};

const LOAD_ITEM_TYPES = ["Household", "Vehicles", "IT Supplies"];
const VEHICLE_ITEM_TYPES = ["Bike", "Car", "Others"];
const EMPTY_LOAD = { load_name: "", description: "", weight: "", volume: "", handle_with_care: false, item_types: ["Household"], vehicle_item_type: "" };
const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

// Safe date formatter — never throws even for empty / invalid strings
const safeFormat = (val, fmt) => {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return "—";
    return format(d, fmt);
  } catch {
    return "—";
  }
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">{label}</p>
    <p className="text-sm font-semibold text-[#0F172A]">{value || "—"}</p>
  </div>
);

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
    {children}
  </div>
);

/* ── QR Print Modal ─────────────────────────────────────────────────────── */
function QRPrintModal({ load, onClose }) {
  const qrRef = useRef(null);

  const handlePrint = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;

    // Clone and ensure xmlns attribute is set so the browser renders it as an image
    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const svgStr = new XMLSerializer().serializeToString(clone);
    const imgSrc = `data:image/svg+xml,${encodeURIComponent(svgStr)}`;

    const win = window.open("", "_blank", "width=400,height=520,toolbar=no,menubar=no,scrollbars=no");
    if (!win) { toast.error("Pop-up blocked. Please allow pop-ups for this site."); return; }

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>QR Code — ${load.load_ref}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      display: flex; align-items: center; justify-content: center;
      min-height: 100vh; background: #fff;
      font-family: 'Courier New', Courier, monospace;
      padding: 2mm;
    }
    .card {
      text-align: center;
      border: 2px solid #1B2A6B;
      border-bottom: 5px solid #FFD700;
      padding: 14px 18px;
      display: inline-block;
    }
    img { width: 150px; height: 150px; display: block; margin: 0 auto; }
    .ref {
      font-size: 18px; font-weight: 900;
      letter-spacing: 4px; color: #1B2A6B;
      margin-top: 10px;
    }
    .name {
      font-size: 10px; color: #475569;
      margin-top: 5px; font-family: sans-serif;
    }
    .hwc {
      margin-top: 8px;
      padding: 5px 10px;
      background: #FFF7ED;
      border: 2px solid #F59E0B;
      color: #92400E;
      font-size: 11px;
      font-weight: 900;
      letter-spacing: 2px;
      text-transform: uppercase;
      font-family: sans-serif;
    }
    @media print {
      @page { size: 70mm ${load.handle_with_care ? "105mm" : "88mm"}; margin: 0; }
      body { padding: 2mm; }
    }
  </style>
</head>
<body>
  <div class="card">
    <img src="${imgSrc}" alt="QR Code for ${load.load_ref}" />
    <p class="ref">${load.load_ref}</p>
    ${load.load_name ? `<p class="name">${load.load_name}</p>` : ""}
    ${load.handle_with_care ? `<div class="hwc">&#9888; Handle with Care</div>` : ""}
  </div>
  <script>window.onload = function () { window.print(); };<\/script>
</body>
</html>`);
    win.document.close();
  };

  if (!load) return null;
  return (
    <Dialog open={!!load} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm rounded-none p-0 gap-0">
        {/* Header */}
        <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700] flex items-center justify-between">
          <div>
            <DialogTitle className="text-white font-bold tracking-wide text-base">QR Code Preview</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">{load.load_name}</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* QR preview */}
        <div className="flex flex-col items-center py-10 px-6 bg-white gap-4">
          <div
            ref={qrRef}
            className="border-2 border-[#1B2A6B] border-b-4 border-b-[#FFD700] p-6 bg-white inline-block"
          >
            <QRCodeSVG
              value={load.load_ref}
              size={180}
              bgColor="#FFFFFF"
              fgColor="#1B2A6B"
              level="H"
            />
          </div>
          {/* Load ID label */}
          <p className="font-mono font-black text-xl tracking-widest text-[#1B2A6B]">{load.load_ref}</p>
          {load.load_name && (
            <p className="text-sm text-[#475569] -mt-2">{load.load_name}</p>
          )}
          {load.handle_with_care && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 px-4 py-2 mt-1">
              <span className="text-amber-600 text-lg">⚠</span>
              <p className="text-xs font-black uppercase tracking-widest text-amber-800">Handle with Care</p>
            </div>
          )}
          <p className="text-[10px] text-[#94A3B8] text-center mt-1">
            The QR encodes the Load ID. Even if unreadable, the ID is printed below.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] px-4 py-2 transition-colors"
          >
            Close
          </button>
          <Button
            onClick={handlePrint}
            className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] flex items-center gap-2"
          >
            <Printer size={13} />
            Print QR
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BookingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [booking, setBooking]       = useState(null);
  const [loads, setLoads]           = useState([]);
  const [activities, setActivities] = useState([]);
  const [transfers, setTransfers]   = useState([]);
  const [showAdd, setShowAdd]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [loadForm, setLoadForm]     = useState(EMPTY_LOAD);
  const [showTransfer, setShowTransfer] = useState(false);
  const [qrLoad, setQrLoad]         = useState(null);
  const [showEditBooking, setShowEditBooking] = useState(false);
  const [editBForm, setEditBForm]   = useState({});
  const [payments, setPayments]     = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: "", notes: "" });
  const [paymentImage, setPaymentImage] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [isRefund, setIsRefund] = useState(false);

  // Pickup Additional Charges
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [showAddCharge, setShowAddCharge]         = useState(false);
  const [chargeForm, setChargeForm]               = useState({ amount: "", reason: "" });
  const [chargeSaving, setChargeSaving]           = useState(false);

  // Transit Insurance
  const [showInsurance, setShowInsurance]   = useState(false);
  const [insuranceForm, setInsuranceForm]   = useState({});
  const [insuranceSaving, setInsuranceSaving] = useState(false);

  const fetchAll = async () => {
    try {
      const [bRes, lRes, aRes, tRes, pRes, cRes] = await Promise.all([
        axios.get(`${API}/confirmed-leads/${id}`,                    { withCredentials: true }),
        axios.get(`${API}/confirmed-leads/${id}/loads`,              { withCredentials: true }),
        axios.get(`${API}/confirmed-leads/${id}/activities`,         { withCredentials: true }),
        axios.get(`${API}/confirmed-leads/${id}/transfers`,          { withCredentials: true }),
        axios.get(`${API}/confirmed-leads/${id}/payments`,           { withCredentials: true }),
        axios.get(`${API}/confirmed-leads/${id}/additional-charges`, { withCredentials: true }),
      ]);
      setBooking(bRes.data);
      setLoads(lRes.data);
      setActivities(aRes.data);
      setTransfers(tRes.data);
      setPayments(pRes.data);
      setAdditionalCharges(cRes.data);
    } catch {
      toast.error("Failed to load booking.");
    }
  };

  useEffect(() => { fetchAll(); }, [id]);

  /* ── Where is this load right now? ───────────────────────────────────── */
  const getLoadLocation = (load) => {
    if (!load.transferred || !load.transfer_id) {
      return { label: "At Pickup", sublabel: booking?.from_location || "—", color: "bg-slate-100 text-slate-600 border-slate-200" };
    }
    const t = transfers.find(tr => tr.id === load.transfer_id);
    if (!t) {
      return { label: "At Pickup", sublabel: booking?.from_location || "—", color: "bg-slate-100 text-slate-600 border-slate-200" };
    }
    const s = t.status;
    if (t.location_confirmed) {
      return { label: s, sublabel: t.current_location || "—", color: "bg-blue-50 text-blue-700 border-blue-200" };
    }
    return { label: "Location needs confirmation", sublabel: t.current_location || "Unknown", color: "bg-amber-50 text-amber-700 border-amber-200" };
  };

  const handlePaymentImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be smaller than 2MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setPaymentImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Enter a valid payment amount.");
      return;
    }
    setPaymentSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/confirmed-leads/${id}/payments`,
        { amount: (isRefund ? -1 : 1) * parseFloat(paymentForm.amount), notes: paymentForm.notes, image_data: paymentImage },
        { withCredentials: true }
      );
      setPayments(prev => [...prev, data]);
      await refreshActivities();
      toast.success(isRefund ? "Refund recorded." : "Payment recorded.");
      setShowPayment(false);
      setPaymentForm({ amount: "", notes: "" });
      setPaymentImage("");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to add payment."));
    } finally { setPaymentSaving(false); }
  };

  const handleCancelBooking = async () => {
    if (!window.confirm("Cancel this confirmed lead?")) return;
    try {
      const { data } = await axios.patch(`${API}/confirmed-leads/${id}`, { status: "Cancelled" }, { withCredentials: true });
      setBooking(data);
      await refreshActivities();
      toast.success("Confirmed lead cancelled.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to cancel confirmed lead."));
    }
  };

  const openEditBooking = () => {    setEditBForm({
      customer_name: booking.customer_name || "",
      alternate_number: booking.alternate_number || "",
      shifting_date: booking.shifting_date || "",
      quotation:     booking.quotation != null ? String(booking.quotation) : "",
      advance_paid:  booking.advance_paid != null ? String(booking.advance_paid) : "",
      load_size:     booking.load_size || "",
      moving_from:   booking.moving_from || "",
      moving_to:     booking.moving_to || "",
      from_location: booking.from_location || "",
      to_location:   booking.to_location || "",
      special_requests: booking.special_requests || "",
    });
    setShowEditBooking(true);
  };

  const handleEditBooking = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.patch(
        `${API}/confirmed-leads/${id}`,
        {
          customer_name: editBForm.customer_name || "",
          alternate_number: editBForm.alternate_number || "",
          shifting_date: editBForm.shifting_date || "",
          quotation:     editBForm.quotation !== "" ? parseFloat(editBForm.quotation) : 0,
          advance_paid:  editBForm.advance_paid !== "" ? parseFloat(editBForm.advance_paid) : 0,
          load_size:     editBForm.load_size || "",
          moving_from:   editBForm.moving_from || "",
          moving_to:     editBForm.moving_to || "",
          from_location: editBForm.from_location || "",
          to_location:   editBForm.to_location || "",
          special_requests: editBForm.special_requests || "",
        },
        { withCredentials: true }
      );
      setBooking(data);
      await refreshActivities();
      toast.success("Confirmed lead updated.");
      setShowEditBooking(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update confirmed lead."));
    }
  };

  /* ── Additional Charges ─────────────────────────────────────────────────── */
  const handleAddCharge = async (e) => {
    e.preventDefault();
    if (!chargeForm.amount || parseFloat(chargeForm.amount) <= 0) {
      toast.error("Enter a valid amount."); return;
    }
    setChargeSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/confirmed-leads/${id}/additional-charges`,
        { amount: parseFloat(chargeForm.amount), reason: chargeForm.reason },
        { withCredentials: true }
      );
      setAdditionalCharges(prev => [...prev, data]);
      await refreshActivities();
      toast.success("Pickup addition recorded.");
      setShowAddCharge(false);
      setChargeForm({ amount: "", reason: "" });
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to add charge."));
    } finally { setChargeSaving(false); }
  };

  /* ── Transit Insurance ───────────────────────────────────────────────────── */
  const openInsuranceEdit = () => {
    setInsuranceForm({
      risk_coverage:          booking.risk_coverage || "",
      carrier_risk_opted:     booking.carrier_risk_opted || "",
      consignment_value:      booking.consignment_value ? String(booking.consignment_value) : "",
      carrier_risk_percentage: booking.carrier_risk_percentage ? String(booking.carrier_risk_percentage) : "2.5",
      insurance_status:       booking.insurance_status || "",
    });
    setShowInsurance(true);
  };

  const handleInsuranceSave = async (e) => {
    e.preventDefault();
    setInsuranceSaving(true);
    try {
      const payload = {
        risk_coverage:      insuranceForm.risk_coverage || null,
        carrier_risk_opted: insuranceForm.carrier_risk_opted || null,
        consignment_value:  insuranceForm.consignment_value ? parseFloat(insuranceForm.consignment_value) : null,
        insurance_status:   insuranceForm.insurance_status || null,
      };
      if (!isRM) {
        // Admin/Manager/WM can change the percentage
        payload.carrier_risk_percentage = parseFloat(insuranceForm.carrier_risk_percentage || "2.5");
      }
      const { data } = await axios.patch(
        `${API}/confirmed-leads/${id}/insurance`, payload, { withCredentials: true }
      );
      setBooking(data);
      await refreshActivities();
      toast.success("Insurance details updated.");
      setShowInsurance(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update insurance."));
    } finally { setInsuranceSaving(false); }
  };

  const refreshActivities = () =>
    Promise.all([
      axios.get(`${API}/confirmed-leads/${id}/activities`,         { withCredentials: true }),
      axios.get(`${API}/confirmed-leads/${id}/transfers`,          { withCredentials: true }),
      axios.get(`${API}/confirmed-leads/${id}/loads`,              { withCredentials: true }),
      axios.get(`${API}/confirmed-leads/${id}/payments`,           { withCredentials: true }),
      axios.get(`${API}/confirmed-leads/${id}/additional-charges`, { withCredentials: true }),
    ]).then(([aRes, tRes, lRes, pRes, cRes]) => {
      setActivities(aRes.data);
      setTransfers(tRes.data);
      setLoads(lRes.data);
      setPayments(pRes.data);
      setAdditionalCharges(cRes.data);
    });

  const handleAddLoad = async (e) => {
    e.preventDefault();
    if (!loadForm.load_name.trim()) { toast.error("Load name is required."); return; }
    if (loadForm.item_types.length === 0) { toast.error("Select at least one item type."); return; }
    if (loadForm.item_types.includes("Vehicles") && !loadForm.vehicle_item_type) { toast.error("Select a vehicle type."); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/confirmed-leads/${id}/loads`, {
        load_name:        loadForm.load_name,
        description:      loadForm.description,
        weight:           loadForm.weight  ? parseFloat(loadForm.weight)  : null,
        volume:           loadForm.volume  ? parseFloat(loadForm.volume)  : null,
        handle_with_care: loadForm.handle_with_care,
        item_types:       loadForm.item_types,
        vehicle_item_type: loadForm.item_types.includes("Vehicles") ? loadForm.vehicle_item_type : null,
      }, { withCredentials: true });
      setLoads(prev => [...prev, data]);
      await refreshActivities();
      toast.success("Load added.");
      setShowAdd(false);
      setLoadForm(EMPTY_LOAD);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to add load."));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLoad = async (loadId, loadName) => {
    if (!window.confirm(`Remove load "${loadName}"?`)) return;
    try {
      await axios.delete(`${API}/confirmed-leads/${id}/loads/${loadId}`, { withCredentials: true });
      setLoads(prev => prev.filter(l => l.id !== loadId));
      await refreshActivities();
      toast.success("Load removed.");
    } catch {
      toast.error("Failed to remove load.");
    }
  };

  if (!booking) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-2 text-[#475569] text-sm">
          <RefreshCw size={16} className="animate-spin" />Loading...
        </div>
      </div>
    );
  }

  // Role-based edit permission (evaluated after booking is loaded)
  const isRM  = authUser?.role === "relationship_manager";
  const isWM  = authUser?.role === "warehouse_manager";
  const isSupervisor = authUser?.role === "supervisor";
  const isAdminOrMgr = ["admin", "superadmin", "manager"].includes(authUser?.role);
  const rmOwnsBooking = authUser?._id === booking?.assigned_to;
  const hasLR = transfers.length > 0;
  const canEdit = !isRM || (rmOwnsBooking && !hasLR);
  // Supervisors, Warehouse Managers and admins can create consignments; RMs remain read-only here.
  const canCreateConsignment = !isRM;
  // Insurance/additional charges editable by: Admin, Manager, WM (any), RM (assigned only)
  const canEditInsurance = isAdminOrMgr || isWM || isSupervisor || (isRM && rmOwnsBooking && !hasLR);
  const canDeleteLoad = isAdminOrMgr;

  const additionalPaid   = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const additionalChargesTotal = additionalCharges.reduce((sum, c) => sum + (c.amount || 0), 0);
  const revisedTotal     = (booking.quotation || 0) + additionalChargesTotal;
  const balance          = revisedTotal - (booking.advance_paid || 0) - additionalPaid;

  return (
    <div className="space-y-5 pb-10" data-testid="booking-detail-page">

      {/* Back + status */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate("/confirmed-leads")}
          data-testid="back-to-bookings"
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#475569] hover:text-[#1B2A6B] transition-colors"
        >
          <ArrowLeft size={14} />Back to Confirmed Leads
        </button>
        <span className="text-[#E2E8F0]">/</span>
        <span className="text-sm font-semibold text-[#0F172A]">{booking.customer_name}</span>
        <span className={`ml-auto inline-flex items-center px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_STYLE[booking.status] || STATUS_STYLE.Active}`}>
          {booking.status}
        </span>
        <button
          data-testid="edit-booking-btn"
          onClick={openEditBooking}
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#475569] hover:text-[#1B2A6B] border border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors px-3 py-1.5"
          style={{ display: canEdit ? undefined : "none" }}
        >
          <Pencil size={12} />Edit Confirmed Lead
        </button>
        {canEdit && booking.status !== "Cancelled" && (
          <button onClick={handleCancelBooking}
            className="text-xs font-bold uppercase tracking-widest text-red-600 border border-red-200 hover:bg-red-50 px-3 py-1.5">
            Cancel Lead
          </button>
        )}
        <Button
          data-testid="create-transfer-btn"
          onClick={() => setShowTransfer(true)}
          className="rounded-none bg-[#FFD700] hover:bg-[#f0c800] text-[#1B2A6B] font-bold tracking-widest uppercase text-xs h-9 px-4 border-b-2 border-[#1B2A6B]"
          style={{ display: canCreateConsignment ? undefined : "none" }}
        >
          + Create Consignment
        </Button>
      </div>

      {/* Customer header */}
      <div className="bg-white border border-[#E2E8F0]">
        <div className="bg-[#1B2A6B] px-6 py-4 border-l-4 border-[#FFD700]">
          <h2 className="text-lg font-bold text-white">{booking.customer_name}</h2>
          <p className="text-white/60 text-xs mt-0.5">
            {booking.phone || "No phone"}{booking.alternate_number ? ` · Alternate: ${booking.alternate_number}` : ""}
          </p>
        </div>
        <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-5">
          <Field label="Moving From"   value={booking.moving_from} />
          <Field label="Moving To"     value={booking.moving_to} />
          <Field label="From Location" value={booking.from_location} />
          <Field label="To Location"   value={booking.to_location} />
          <Field label="Load Size"     value={booking.load_size} />
          <Field label="Shifting Date" value={booking.shifting_date ? safeFormat(booking.shifting_date + "T00:00:00", "dd MMM yyyy") : null} />
          <Field label="Booked On"     value={safeFormat(booking.created_at, "dd MMM yyyy")} />
          <Field label="Alternate Number" value={booking.alternate_number} />
        </div>
      </div>

      {/* Special Requests */}
      {booking.special_requests && (
        <div className="bg-amber-50 border border-amber-200 p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-2">Special Requests</p>
          <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{booking.special_requests}</p>
        </div>
      )}

      {/* Financial summary — 4 cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Card 1: Base Quotation */}
        <div className="bg-white border border-[#E2E8F0] p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-1">Quotation</p>
          <p className="text-xl font-bold text-[#1B2A6B]">{formatINR(booking.quotation)}</p>
        </div>

        {/* Card 2: Pickup Additions */}
        <div className="bg-white border border-[#E2E8F0] p-4">
          <div className="flex items-start justify-between gap-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-1">Pickup Additions</p>
            {canEditInsurance && (
              <button
                data-testid="add-charge-inline-btn"
                onClick={() => { setChargeForm({ amount: "", reason: "" }); setShowAddCharge(true); }}
                className="text-[9px] font-bold uppercase tracking-wider text-[#1B2A6B] border border-[#C7D2FE] bg-[#EEF2FF] hover:bg-[#1B2A6B] hover:text-white transition-colors px-1.5 py-0.5 shrink-0"
              >+ Add</button>
            )}
          </div>
          <p className="text-xl font-bold text-orange-600">{formatINR(additionalChargesTotal)}</p>
          {additionalCharges.length > 0 && (
            <p className="text-[10px] text-[#94A3B8] mt-0.5">{additionalCharges.length} item{additionalCharges.length !== 1 ? "s" : ""}</p>
          )}
        </div>

        {/* Card 3: Total Paid */}
        <div className="bg-white border border-[#E2E8F0] p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-1">Total Paid</p>
          <p className="text-xl font-bold text-green-600">{formatINR((booking.advance_paid || 0) + additionalPaid)}</p>
          <p className="text-[10px] text-[#94A3B8] mt-0.5">
            Adv {formatINR(booking.advance_paid || 0)}
            {additionalPaid > 0 && ` + ${formatINR(additionalPaid)}`}
          </p>
        </div>

        {/* Card 4: Balance / TO-PAY */}
        <div className={`border p-4 ${balance > 0 ? "border-amber-200 bg-amber-50" : "bg-white border-[#E2E8F0]"}`}>
          <div className="flex items-start justify-between gap-1">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-1">Balance / TO-PAY</p>
              <p className={`text-xl font-bold ${balance > 0 ? "text-amber-600" : "text-[#475569]"}`}>{formatINR(balance)}</p>
              {additionalChargesTotal > 0 && (
                <p className="text-[10px] text-[#94A3B8] mt-0.5">Revised: {formatINR(revisedTotal)}</p>
              )}
            </div>
            {canEdit && (
              <button
                data-testid="add-payment-btn"
                onClick={() => { setIsRefund(false); setPaymentForm({ amount: "", notes: "" }); setPaymentImage(""); setShowPayment(true); }}
                className="text-[9px] font-bold uppercase tracking-wider text-[#1B2A6B] border border-[#1B2A6B] hover:bg-[#1B2A6B] hover:text-white transition-colors px-1.5 py-0.5 shrink-0"
              ><CreditCard size={10} className="inline mr-0.5" />Pay</button>
            )}
            {canEdit && <button onClick={() => { setIsRefund(true); setPaymentForm({ amount: "", notes: "Token refund" }); setPaymentImage(""); setShowPayment(true); }}
              className="text-[9px] font-bold uppercase tracking-wider text-red-600 border border-red-200 hover:bg-red-50 px-1.5 py-0.5 shrink-0">Refund</button>}
          </div>
        </div>
      </div>

      {/* Transit Insurance section */}
      <div className="bg-white border border-[#E2E8F0]">
        <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Warehouse size={13} className="text-[#475569]" />
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">Transit Insurance</h3>
          </div>
          {canEditInsurance && (
            <button
              data-testid="edit-insurance-btn"
              onClick={openInsuranceEdit}
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#475569] hover:text-[#1B2A6B] border border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors px-2.5 py-1"
            >
              <Pencil size={10} />Edit Insurance
            </button>
          )}
        </div>
        <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-5 gap-x-8 gap-y-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Risk Coverage</p>
            <p className="text-sm font-semibold text-[#0F172A] mt-0.5">{booking.risk_coverage || "—"}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Carrier's Risk Opted</p>
            <p className="text-sm font-semibold text-[#0F172A] mt-0.5">{booking.carrier_risk_opted || "—"}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Consignment Value</p>
            <p className="text-sm font-semibold text-[#0F172A] mt-0.5">{booking.consignment_value > 0 ? formatINR(booking.consignment_value) : "—"}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Coverage Amount ({booking.carrier_risk_percentage || 2.5}%)</p>
            <p className="text-sm font-bold text-[#1B2A6B] mt-0.5">{booking.carrier_risk_amount > 0 ? formatINR(booking.carrier_risk_amount) : "—"}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">Insurance Status</p>
            {booking.insurance_status ? (
              <span className={`inline-flex items-center mt-0.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${
                booking.insurance_status === "Collected"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}>
                {booking.insurance_status}
              </span>
            ) : (
              <p className="text-sm text-[#CBD5E1] mt-0.5 italic text-xs">Not set</p>
            )}
          </div>
        </div>
        {/* Insurance note — independent of balance */}
        {booking.carrier_risk_amount > 0 && (
          <div className="px-5 pb-4">
            <p className="text-[10px] text-[#94A3B8] italic">
              Insurance amount of {formatINR(booking.carrier_risk_amount)} is collected independently and is not included in the balance calculation above.
            </p>
          </div>
        )}
      </div>

      {/* Loads + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left: Loads (2/3 width) */}
        <div className="lg:col-span-2 space-y-4">

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#1B2A6B]">Loads</h3>
              <p className="text-xs text-[#475569] mt-0.5">{loads.length} load{loads.length !== 1 ? "s" : ""} in this booking</p>
            </div>
            <Button
              data-testid="add-load-btn"
              onClick={() => { setLoadForm(EMPTY_LOAD); setShowAdd(true); }}
              className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-4 border-b-2 border-[#FFD700]"
              style={{ display: canEdit ? undefined : "none" }}
            >
              <Plus size={13} className="mr-1.5" />Add Load
            </Button>
          </div>

          {loads.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] flex flex-col items-center justify-center py-12 gap-3">
              <Package size={36} className="text-[#E2E8F0]" />
              <p className="text-sm text-[#94A3B8]">No loads yet.{" "}
                <button onClick={() => setShowAdd(true)} className="text-[#1B2A6B] font-semibold underline">Add the first load</button>
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {loads.map((load, idx) => (
                <div key={load.id} data-testid={`load-card-${idx}`} className="bg-white border border-[#E2E8F0] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="font-semibold text-[#0F172A] text-sm">{load.load_name}</span>
                        <span className="text-[10px] font-bold tracking-wider text-[#1B2A6B] bg-[#EEF0FA] px-2 py-0.5 font-mono">{load.load_ref}</span>
                        {load.handle_with_care && (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-sm">
                            ⚠ Handle with Care
                          </span>
                        )}
                        {(load.item_types || ["Household"]).map(itemType => (
                          <span key={itemType} className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded-sm">
                            {itemType === "Vehicles" ? (load.vehicle_item_type === "Others" || !load.vehicle_item_type ? "Vehicles" : load.vehicle_item_type) : itemType}
                          </span>
                        ))}
                        {load.transferred && (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded-sm">
                            {load.lr_number || "Transferred"}
                          </span>
                        )}
                      </div>
                      {/* Current location */}
                      {(() => {
                        const loc = getLoadLocation(load);
                        return (
                          <div className={`inline-flex items-center gap-1.5 mt-2 px-2 py-1 border text-[10px] font-bold uppercase tracking-wider rounded-sm ${loc.color}`}>
                            <MapPin size={9} />
                            <span>{loc.label}</span>
                            <span className="font-normal normal-case opacity-75">— {loc.sublabel}</span>
                          </div>
                        );
                      })()}
                      {load.description && (
                        <p className="text-xs text-[#475569] mt-1 leading-relaxed">{load.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {load.weight != null && (
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 font-medium">{load.weight} kg</span>
                        )}
                        {load.volume != null && (
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 font-medium">{load.volume} m³</span>
                        )}
                        <span className="text-[10px] text-[#94A3B8]">{safeFormat(load.created_at, "dd MMM yyyy, hh:mm a")}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        data-testid={`print-qr-${idx}`}
                        onClick={() => setQrLoad(load)}
                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-2 py-1.5 rounded-sm"
                        title="Print QR Code"
                      >
                        <Printer size={11} />
                        Print QR
                      </button>
                      <button
                        data-testid={`delete-load-${idx}`}
                        onClick={() => handleDeleteLoad(load.id, load.load_name)}
                        className="text-[#CBD5E1] hover:text-red-500 transition-colors p-1 shrink-0"
                        title="Remove load (Admin only)"
                        style={{ display: canDeleteLoad ? undefined : "none" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {booking.lead_id && (
            <button
              data-testid="view-source-lead"
              onClick={() => navigate(`/leads/${booking.lead_id}`)}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1B2A6B] border border-[#1B2A6B] hover:bg-[#1B2A6B] hover:text-white transition-colors px-4 py-2.5"
            >
              <ExternalLink size={13} />View Source Lead
            </button>
          )}
        </div>

        {/* Right: Activity Timeline (1/3 width) */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E2E8F0] p-6 h-full">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569] mb-5">Activity Timeline</h3>

            {activities.length === 0 ? (
              <p className="text-xs text-[#94A3B8] text-center py-8">No activity recorded yet.</p>
            ) : (
              <div className="space-y-1">
                {[...activities].reverse().map((act, idx, arr) => (
                  <div key={act.id || idx} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 mt-0.5 ${ACT_DOT[act.action] || "bg-gray-400"}`} />
                      {idx < arr.length - 1 && <div className="w-px flex-1 min-h-[24px] bg-[#E2E8F0] mt-1" />}
                    </div>
                    <div className="pb-4 min-w-0">
                      <p className="text-xs text-[#0F172A] leading-snug">{act.description}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] text-[#94A3B8]">{act.created_by}</span>
                        <span className="text-[#E2E8F0]">·</span>
                        <span className="text-[10px] text-[#94A3B8]">{safeFormat(act.created_at, "dd MMM, hh:mm a")}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Transfers + Payments ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Transfers (2/3) */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#1B2A6B]">Transfers</h3>
              <p className="text-xs text-[#475569] mt-0.5">{transfers.length} transfer{transfers.length !== 1 ? "s" : ""} for this booking</p>
            </div>
          </div>

        {transfers.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] flex flex-col items-center justify-center py-10 gap-2">
            <Truck size={32} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No transfers created yet for this booking.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E2E8F0]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                  {["LR Number", "Trip Sheet", "Status / Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transfers.map((t, idx) => {
                  const statusCls =
                    t.status === "Complete" || t.status === "Completed" ? "bg-green-50 text-green-700 border-green-200" :
                    t.status === "In Transit" ? "bg-amber-50 text-amber-700 border-amber-200" :
                    t.status === "On Hold"    ? "bg-red-50 text-red-600 border-red-200" :
                                                "bg-blue-50 text-blue-700 border-blue-200";
                  const rowBg = idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]";
                  return (
                    <React.Fragment key={t.id}>
                      <tr className={rowBg}>
                        <td className="px-4 pt-3 pb-1 whitespace-nowrap">
                          <span className="font-mono font-black text-sm text-[#1B2A6B] bg-[#EEF0FA] px-2.5 py-1 border border-[#C7D2FE] tracking-widest">
                            {t.lr_number || "—"}
                          </span>
                        </td>
                        <td className="px-4 pt-3 pb-1 whitespace-nowrap">
                          {t.consignment_number ? (
                            <span className="font-mono font-bold text-xs text-[#475569] bg-[#F9FAFB] px-2 py-1 border border-[#E2E8F0]">
                              {t.consignment_number}
                            </span>
                          ) : <span className="text-xs text-[#CBD5E1] italic">No trip sheet</span>}
                        </td>
                        <td className="px-4 pt-3 pb-1">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${statusCls}`}>
                              {t.status || "Scheduled"}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {/* Row 2 — secondary details */}
                      <tr className={`border-b border-[#E2E8F0] last:border-b-0 ${rowBg}`}>
                        <td colSpan={3} className="px-4 pt-0.5 pb-3">
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-0.5 text-xs text-[#64748B]">
                            {t.transfer_date && (
                              <span><span className="font-medium text-[#475569]">Date:</span> {safeFormat(t.transfer_date + "T00:00:00", "dd MMM yyyy")}</span>
                            )}
                            <span className="flex items-center gap-1">
                              <MapPin size={10} className="text-[#94A3B8]" />
                              <span className="text-[#475569]">{t.origin_label}</span>
                              <ArrowRight size={10} className="text-[#CBD5E1]" />
                              <span className="text-[#475569]">{t.destination_label}</span>
                            </span>
                            {t.vehicle_display && (
                              <span className="flex items-center gap-1">
                                <Truck size={10} className="text-[#94A3B8]" />
                                {t.vehicle_display}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Package size={10} className="text-[#94A3B8]" />
                              {(t.load_ids || []).length} item{(t.load_ids || []).length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </div>{/* end Transfers col */}

        {/* Right col: Additional Charges + Payments (1/3) */}
        <div className="lg:col-span-1 space-y-4">

          {/* ── Pickup Additional Charges ─────────────────────────── */}
          <div className="bg-white border border-[#E2E8F0]">
            <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
              <div>
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">Pickup Additions</h3>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">Increases total quotation & balance</p>
              </div>
              {canEditInsurance && (
                <button
                  data-testid="add-charge-btn"
                  onClick={() => { setChargeForm({ amount: "", reason: "" }); setShowAddCharge(true); }}
                  className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-2 py-1"
                >
                  <Plus size={10} />Add
                </button>
              )}
            </div>
            <div className="p-4 space-y-2 max-h-[200px] overflow-y-auto">
              {additionalCharges.length === 0 ? (
                <p className="text-xs text-[#94A3B8] text-center py-4">No additional charges recorded.</p>
              ) : (
                additionalCharges.map((c, idx) => (
                  <div key={c.id || idx} className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-bold text-orange-600">{formatINR(c.amount)}</p>
                      {c.reason && <p className="text-[11px] text-[#475569] truncate">{c.reason}</p>}
                      <p className="text-[10px] text-[#94A3B8]">{safeFormat(c.created_at, "dd MMM")} · {c.created_by}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            {additionalCharges.length > 0 && (
              <div className="px-5 py-2.5 border-t border-[#E2E8F0] bg-[#F9FAFB] flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">Total Additions</span>
                <span className="text-sm font-black text-orange-600">{formatINR(additionalChargesTotal)}</span>
              </div>
            )}
          </div>

          {/* ── Payments ──────────────────────────────────────────── */}
          <div className="bg-white border border-[#E2E8F0]">
            <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">Payments</h3>
              <button
                data-testid="add-payment-header-btn"
                onClick={() => { setIsRefund(false); setPaymentForm({ amount: "", notes: "" }); setPaymentImage(""); setShowPayment(true); }}
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-2 py-1"
                style={{ display: canEdit ? undefined : "none" }}
              >
                <CreditCard size={10} />Add
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[280px]">
              {(booking.advance_paid > 0) && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#1B2A6B] mt-0.5" />
                    {(payments.length > 0) && <div className="w-px flex-1 min-h-[24px] bg-[#E2E8F0] mt-1" />}
                  </div>
                  <div className="pb-3 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <p className="text-base font-black text-[#1B2A6B]">{formatINR(booking.advance_paid)}</p>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Advance</span>
                    </div>
                    <p className="text-[11px] text-[#94A3B8] mt-0.5">
                      {safeFormat(booking.created_at, "dd MMM yyyy")}
                    </p>
                  </div>
                </div>
              )}

              {payments.length === 0 && !booking.advance_paid && (
                <p className="text-xs text-[#94A3B8] text-center py-6">No payments recorded yet.</p>
              )}

              {payments.map((p, idx) => (
                <div key={p.id} className="flex gap-3">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-0.5" />
                    {idx < payments.length - 1 && <div className="w-px flex-1 min-h-[24px] bg-[#E2E8F0] mt-1" />}
                  </div>
                  <div className="pb-3 min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <p className="text-base font-black text-emerald-600">{formatINR(p.amount)}</p>
                    </div>
                    {p.notes && <p className="text-xs text-[#475569] mt-0.5 truncate">{p.notes}</p>}
                    <p className="text-[11px] text-[#94A3B8] mt-0.5">
                      {safeFormat(p.created_at, "dd MMM yyyy")} · {p.created_by}
                    </p>
                    {p.image_data && (
                      <a href={p.image_data} target="_blank" rel="noreferrer">
                        <img src={p.image_data} alt="Receipt"
                          className="mt-1.5 max-w-[120px] max-h-[80px] object-cover border border-[#E2E8F0] cursor-pointer hover:opacity-80 transition-opacity"
                        />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 py-3 border-t border-[#E2E8F0] bg-[#F9FAFB]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">Total Received</span>
                <span className="text-sm font-black text-[#1B2A6B]">
                  {formatINR((booking.advance_paid || 0) + additionalPaid)}
                </span>
              </div>
            </div>
          </div>

        </div>{/* end right col */}

      </div>{/* end Transfers + Payments grid */}

      {/* Create Transfer Dialog */}
      <CreateTransferDialog
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        initialBooking={booking}
        onCreated={() => refreshActivities()}
        userRole={authUser?.role}
      />

      {/* QR Print Modal */}
      <QRPrintModal load={qrLoad} onClose={() => setQrLoad(null)} />

      {/* Add Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={o => { if (!o) setShowPayment(false); }}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base flex items-center gap-2">
              <CreditCard size={15} />{isRefund ? "Process Refund" : "Add Payment"}
            </DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">{booking.customer_name}</p>
          </div>
          <form onSubmit={handleAddPayment}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="Amount (₹) *">
                <Input
                  type="number" min="1" step="0.01"
                  value={paymentForm.amount}
                  onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 25000"
                  data-testid="payment-amount-input"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Notes (optional)">
                <Input
                  value={paymentForm.notes}
                  onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. UPI transfer, Cash"
                  className={INPUT_CLS}
                />
              </FormField>
              <div className="space-y-2">
                <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">
                  Receipt Image (optional)
                </label>
                <label
                  data-testid="payment-image-upload"
                  className="flex items-center gap-2 cursor-pointer border border-dashed border-[#CBD5E1] hover:border-[#1B2A6B] px-4 py-3 transition-colors"
                >
                  <ImagePlus size={16} className="text-[#94A3B8]" />
                  <span className="text-xs text-[#64748B]">
                    {paymentImage ? "Image selected — click to change" : "Click to upload receipt"}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={handlePaymentImageChange} />
                </label>
                {paymentImage && (
                  <div className="relative inline-block">
                    <img src={paymentImage} alt="Preview" className="max-h-[100px] max-w-full border border-[#E2E8F0]" />
                    <button
                      type="button"
                      onClick={() => setPaymentImage("")}
                      className="absolute top-1 right-1 bg-white/90 text-red-500 p-0.5 border border-red-200"
                    >
                      <X size={11} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => setShowPayment(false)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
                Cancel
              </button>
              <Button type="submit" data-testid="save-payment-btn" disabled={paymentSaving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {paymentSaving ? "Saving..." : isRefund ? "Process Refund" : "Record Payment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={showEditBooking} onOpenChange={o => { if (!o) setShowEditBooking(false); }}>
        <DialogContent className="max-w-lg rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Edit Confirmed Lead</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">{booking.customer_name}</p>
          </div>
          <form onSubmit={handleEditBooking}>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              <FormField label="Customer Name">
                <Input
                  value={editBForm.customer_name || ""}
                  onChange={e => setEditBForm(f => ({ ...f, customer_name: e.target.value }))}
                  placeholder="Customer full name"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Alternate Number">
                <Input
                  value={editBForm.alternate_number || ""}
                  onChange={e => setEditBForm(f => ({ ...f, alternate_number: e.target.value }))}
                  placeholder="Alternate contact number"
                  inputMode="tel"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Shifting Date">
                <Input
                  type="date"
                  value={editBForm.shifting_date || ""}
                  onChange={e => setEditBForm(f => ({ ...f, shifting_date: e.target.value }))}
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Moving From">
                <Input
                  value={editBForm.moving_from || ""}
                  onChange={e => setEditBForm(f => ({ ...f, moving_from: e.target.value }))}
                  placeholder="e.g. Mumbai"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Moving To">
                <Input
                  value={editBForm.moving_to || ""}
                  onChange={e => setEditBForm(f => ({ ...f, moving_to: e.target.value }))}
                  placeholder="e.g. Pune"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="From Location">
                <Input
                  value={editBForm.from_location || ""}
                  onChange={e => setEditBForm(f => ({ ...f, from_location: e.target.value }))}
                  placeholder="e.g. Andheri West"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="To Location">
                <Input
                  value={editBForm.to_location || ""}
                  onChange={e => setEditBForm(f => ({ ...f, to_location: e.target.value }))}
                  placeholder="e.g. Kothrud, Pune"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Load Size">
                <Input
                  value={editBForm.load_size || ""}
                  onChange={e => setEditBForm(f => ({ ...f, load_size: e.target.value }))}
                  placeholder="e.g. 2 BHK"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Quotation (₹)">
                <Input
                  type="number" min="0"
                  value={editBForm.quotation || ""}
                  onChange={e => setEditBForm(f => ({ ...f, quotation: e.target.value }))}
                  placeholder="0"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label="Advance Paid (₹)">
                <Input
                  type="number" min="0"
                  value={editBForm.advance_paid || ""}
                  onChange={e => setEditBForm(f => ({ ...f, advance_paid: e.target.value }))}
                  placeholder="0"
                  className={INPUT_CLS}
                />
              </FormField>
              <div className="sm:col-span-2">
                <FormField label="Special Requests">
                  <textarea
                    value={editBForm.special_requests || ""}
                    onChange={e => setEditBForm(f => ({ ...f, special_requests: e.target.value }))}
                    placeholder="Any special instructions..."
                    rows={3}
                    className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] resize-none"
                  />
                </FormField>
              </div>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => setShowEditBooking(false)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
                Cancel
              </button>
              <Button type="submit" data-testid="save-booking-edit-btn"
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700]">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Load Dialog */}
      <Dialog open={showAdd} onOpenChange={o => { if (!o) setLoadForm(EMPTY_LOAD); setShowAdd(o); }}>
        <DialogContent className="max-w-md rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Add Load</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">A unique load ID (LD-XXXXXX) will be auto-generated</p>
          </div>
          <form onSubmit={handleAddLoad}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="Load Name *">
                <Input
                  value={loadForm.load_name}
                  onChange={e => setLoadForm(f => ({ ...f, load_name: e.target.value }))}
                  placeholder="e.g. Living Room Furniture"
                  data-testid="load-name-input"
                  className={INPUT_CLS}
                  autoFocus
                />
              </FormField>

              <FormField label="Description">
                <textarea
                  value={loadForm.description}
                  onChange={e => setLoadForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. Sofa, 2 chairs, coffee table, TV unit..."
                  rows={3}
                  data-testid="load-description-input"
                  className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] resize-none"
                />
              </FormField>

              <FormField label="Item Type *">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="load-item-types">
                  {LOAD_ITEM_TYPES.map(itemType => {
                    const checked = loadForm.item_types.includes(itemType);
                    return (
                      <label key={itemType} className={`flex items-center gap-2 cursor-pointer border px-3 py-2.5 text-xs font-semibold transition-colors ${checked ? "border-[#1B2A6B] bg-[#EEF0FA] text-[#1B2A6B]" : "border-[#E2E8F0] text-[#475569] hover:border-[#CBD5E1]"}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setLoadForm(f => {
                            const item_types = checked
                              ? f.item_types.filter(type => type !== itemType)
                              : [...f.item_types, itemType];
                            return {
                              ...f,
                              item_types,
                              vehicle_item_type: itemType === "Vehicles" && checked ? "" : f.vehicle_item_type,
                            };
                          })}
                          className="accent-[#1B2A6B] w-4 h-4 shrink-0"
                        />
                        {itemType}
                      </label>
                    );
                  })}
                </div>
              </FormField>

              {loadForm.item_types.includes("Vehicles") && (
                <FormField label="Vehicle Type *">
                  <div className="grid grid-cols-3 gap-2" data-testid="load-vehicle-types">
                    {VEHICLE_ITEM_TYPES.map(vehicleType => (
                      <label key={vehicleType} className={`flex items-center justify-center gap-2 cursor-pointer border px-3 py-2.5 text-xs font-semibold transition-colors ${loadForm.vehicle_item_type === vehicleType ? "border-[#1B2A6B] bg-[#EEF0FA] text-[#1B2A6B]" : "border-[#E2E8F0] text-[#475569] hover:border-[#CBD5E1]"}`}>
                        <input
                          type="radio"
                          name="vehicle-item-type"
                          value={vehicleType}
                          checked={loadForm.vehicle_item_type === vehicleType}
                          onChange={() => setLoadForm(f => ({ ...f, vehicle_item_type: vehicleType }))}
                          className="accent-[#1B2A6B] w-4 h-4 shrink-0"
                        />
                        {vehicleType}
                      </label>
                    ))}
                  </div>
                </FormField>
              )}

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Weight (kg) — optional">
                  <Input
                    type="number" step="any" min="0"
                    value={loadForm.weight}
                    onChange={e => setLoadForm(f => ({ ...f, weight: e.target.value }))}
                    placeholder="e.g. 150"
                    data-testid="load-weight-input"
                    className={INPUT_CLS}
                  />
                </FormField>
                <FormField label="Volume (m³) — optional">
                  <Input
                    type="number" step="any" min="0"
                    value={loadForm.volume}
                    onChange={e => setLoadForm(f => ({ ...f, volume: e.target.value }))}
                    placeholder="e.g. 4.5"
                    data-testid="load-volume-input"
                    className={INPUT_CLS}
                  />
                </FormField>
              </div>

              {/* Handle with Care */}
              <label className="flex items-center gap-3 cursor-pointer bg-amber-50 border border-amber-200 px-4 py-3 hover:bg-amber-100 transition-colors">
                <input
                  type="checkbox"
                  checked={loadForm.handle_with_care}
                  onChange={e => setLoadForm(f => ({ ...f, handle_with_care: e.target.checked }))}
                  data-testid="load-handle-with-care"
                  className="accent-amber-500 w-4 h-4 shrink-0"
                />
                <div>
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-widest">Handle with Care</p>
                  <p className="text-[10px] text-amber-600 mt-0.5">This will be printed on the QR label</p>
                </div>
              </label>
            </div>

            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button
                type="button"
                onClick={() => { setLoadForm(EMPTY_LOAD); setShowAdd(false); }}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                data-testid="save-load-btn"
                disabled={saving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60"
              >
                {saving ? "Adding..." : "Add Load"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Transit Insurance Dialog ─────────────────────────────────────── */}
      <Dialog open={showInsurance} onOpenChange={o => { if (!o) setShowInsurance(false); }}>
        <DialogContent className="max-w-lg rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Transit Insurance</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">{booking.customer_name}</p>
          </div>
          <form onSubmit={handleInsuranceSave}>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              <FormField label="Risk Coverage">
                <select
                  value={insuranceForm.risk_coverage || ""}
                  onChange={e => setInsuranceForm(f => ({ ...f, risk_coverage: e.target.value }))}
                  className="w-full border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none h-11 px-3 text-sm text-[#0F172A] rounded-none bg-white"
                >
                  <option value="">Select...</option>
                  <option value="Owner's Risk">Owner's Risk</option>
                  <option value="Carrier's Risk">Carrier's Risk</option>
                </select>
              </FormField>
              <FormField label="Carrier's Risk Coverage Opted">
                <select
                  value={insuranceForm.carrier_risk_opted || ""}
                  onChange={e => setInsuranceForm(f => ({ ...f, carrier_risk_opted: e.target.value }))}
                  className="w-full border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none h-11 px-3 text-sm text-[#0F172A] rounded-none bg-white"
                >
                  <option value="">Select...</option>
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </select>
              </FormField>
              <FormField label="Value of Consignment (₹)">
                <Input
                  type="number" min="0"
                  value={insuranceForm.consignment_value || ""}
                  onChange={e => setInsuranceForm(f => ({ ...f, consignment_value: e.target.value }))}
                  placeholder="e.g. 200000"
                  className={INPUT_CLS}
                />
              </FormField>
              <FormField label={`Coverage % ${!isAdminOrMgr ? "(Admin only)" : ""}`}>
                <Input
                  type="number" min="0" max="100" step="0.1"
                  value={insuranceForm.carrier_risk_percentage || "2.5"}
                  onChange={e => setInsuranceForm(f => ({ ...f, carrier_risk_percentage: e.target.value }))}
                  disabled={!isAdminOrMgr}
                  className={`${INPUT_CLS} ${!isAdminOrMgr ? "bg-[#F9FAFB] text-[#94A3B8] cursor-not-allowed" : ""}`}
                />
              </FormField>
              <div className="sm:col-span-2 bg-[#EEF2FF] border border-[#C7D2FE] px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] mb-0.5">Auto-Calculated Coverage Amount</p>
                <p className="text-lg font-black text-[#1B2A6B]">
                  {insuranceForm.consignment_value
                    ? formatINR(parseFloat(insuranceForm.consignment_value || 0) * parseFloat(insuranceForm.carrier_risk_percentage || 2.5) / 100)
                    : "—"}
                </p>
                <p className="text-[10px] text-[#6366F1] mt-0.5">
                  This amount is independent and does not affect the balance/TO-PAY calculation.
                </p>
              </div>
              <FormField label="Insurance Status">
                <select
                  value={insuranceForm.insurance_status || ""}
                  onChange={e => setInsuranceForm(f => ({ ...f, insurance_status: e.target.value }))}
                  className="w-full border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none h-11 px-3 text-sm text-[#0F172A] rounded-none bg-white"
                >
                  <option value="">Not set</option>
                  <option value="Pending">Pending</option>
                  <option value="Collected">Collected</option>
                </select>
              </FormField>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => setShowInsurance(false)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
                Cancel
              </button>
              <Button type="submit" data-testid="save-insurance-btn" disabled={insuranceSaving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {insuranceSaving ? "Saving..." : "Save Insurance"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Add Pickup Additional Charge Dialog ──────────────────────────── */}
      <Dialog open={showAddCharge} onOpenChange={o => { if (!o) setShowAddCharge(false); }}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Add Pickup Charge</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Increases total quotation and affects TO-PAY</p>
          </div>
          <form onSubmit={handleAddCharge}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="Amount (₹) *">
                <Input
                  type="number" min="1"
                  value={chargeForm.amount}
                  onChange={e => setChargeForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 2000"
                  className={INPUT_CLS}
                  autoFocus
                  data-testid="charge-amount-input"
                />
              </FormField>
              <FormField label="Reason">
                <Input
                  value={chargeForm.reason}
                  onChange={e => setChargeForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. Extra labour, Rope pulling"
                  className={INPUT_CLS}
                  data-testid="charge-reason-input"
                />
              </FormField>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => setShowAddCharge(false)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
                Cancel
              </button>
              <Button type="submit" data-testid="save-charge-btn" disabled={chargeSaving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {chargeSaving ? "Saving..." : "Add Charge"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
